import { Link } from '@tanstack/react-router'
import { Gauge, Lock } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  Badge,
  BarChart,
  Button,
  buttonVariants,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  Skeleton,
  Spinner,
} from '@/components/ui'
import { useCurrentTenant } from '@/features/tenants/useCurrentTenant'
import { translateEnumValue } from '@/lib/formatting'
import { cn } from '@/lib/utils'

import {
  CancelSection,
  InvoicesSection,
  OpenInvoiceCard,
  ProfileSection,
} from './components/InvoiceSections'
import { PausedForCreditsAlert } from './components/PausedForCredits'
import { PacksSection } from './components/PacksSection'
import { PlansSection } from './components/PlansSection'
import { formatCount, formatShortDate } from './format'
import { useBillingOverview, useUsage, useUsageSummary } from './queries'
import { USAGE_WINDOW_OPTIONS, type UsageDetail, type UsageWindowOption } from './types'

/**
 * Planos e uso (`04` §4.4, recorte do WP-MON-02): "Estado atual" (qualquer membro) e "Uso neste período"
 * (`billing.view`; sem a capability, estado honesto como na Equipe). A seção Planos chega no WP-MON-03: não há
 * checkout que ainda não existe (design/12 §5).
 */
export function BillingPage() {
  const { t, i18n } = useTranslation()
  const { tenant } = useCurrentTenant()
  const [days, setDays] = useState<UsageWindowOption>(30)
  const summaryQuery = useUsageSummary(tenant?.id)
  const usageQuery = useUsage(tenant?.id, days)
  const overviewQuery = useBillingOverview(tenant?.id)
  const overview = overviewQuery.data ?? null

  if (tenant === null || summaryQuery.isLoading) {
    return (
      <div className="flex h-full items-center justify-center py-12">
        <Spinner label={t('loading')} />
      </div>
    )
  }

  const summary = summaryQuery.data
  const stateLabels = t('billing.states', { returnObjects: true }) as Record<string, string>

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-xl font-semibold text-foreground-primary">{t('billing.title')}</h1>
        <p className="text-sm text-foreground-muted">{t('billing.subtitle')}</p>
      </header>

      <PausedForCreditsAlert tenantId={tenant.id} tenantSlug={tenant.slug} />

      <Card>
        <CardHeader>
          <CardTitle>{t('billing.current.title')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3 text-sm">
          {summaryQuery.isError || summary === undefined ? (
            <Alert variant="danger" title={t('billing.current.loadError')}>
              <Button
                variant="secondary"
                size="sm"
                className="mt-2"
                onClick={() => {
                  void summaryQuery.refetch()
                }}
              >
                {t('retry')}
              </Button>
            </Alert>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <Badge
                  variant={
                    summary.state === 'Trial' || summary.state === 'Active'
                      ? 'success'
                      : summary.state === 'PastDue'
                        ? 'warning'
                        : 'neutral'
                  }
                  data-testid="billing-state"
                >
                  {translateEnumValue(stateLabels, summary.state)}
                </Badge>
                {summary.trialEndsAt !== null ? (
                  <span className="text-foreground-muted">
                    {t('billing.current.trialEndsAt', {
                      date: formatShortDate(summary.trialEndsAt, i18n.language),
                    })}
                  </span>
                ) : null}
                {summary.currentPeriodEnd !== null ? (
                  <span className="text-foreground-muted">
                    {t('billing.current.periodEnd', {
                      date: formatShortDate(summary.currentPeriodEnd, i18n.language),
                    })}
                  </span>
                ) : null}
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-foreground-muted">{t('billing.current.available')}</span>
                <span
                  className="text-2xl font-semibold text-foreground-primary"
                  data-testid="billing-available"
                >
                  {formatCount(Math.max(0, summary.available), i18n.language)}
                </span>
              </div>
              {overview === null && overviewQuery.isSuccess ? (
                <p className="text-foreground-muted">{t('billing.noPermission.description')}</p>
              ) : null}
            </>
          )}
        </CardContent>
      </Card>

      {overview !== null && overview.openInvoice !== null ? (
        <OpenInvoiceCard tenantId={tenant.id} invoice={overview.openInvoice} />
      ) : null}

      {overview !== null ? <PlansSection tenantId={tenant.id} overview={overview} /> : null}

      {overview !== null ? <PacksSection tenantId={tenant.id} overview={overview} /> : null}
      <section className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-base font-semibold text-foreground-primary">
            {t('billing.usage.title')}
          </h2>
          <div
            role="group"
            aria-label={t('billing.usage.windowLabel')}
            className="flex gap-1 rounded-md border border-border-default p-0.5"
          >
            {USAGE_WINDOW_OPTIONS.map((option) => (
              <button
                key={option}
                type="button"
                aria-pressed={option === days}
                onClick={() => {
                  setDays(option)
                }}
                className={cn(
                  'rounded px-2.5 py-1 text-xs font-medium',
                  option === days
                    ? 'bg-background-elevated text-foreground-primary'
                    : 'text-foreground-muted hover:text-foreground-primary',
                )}
              >
                {t('billing.usage.window', { days: option })}
              </button>
            ))}
          </div>
        </div>

        {usageQuery.isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : usageQuery.isError ? (
          <Alert variant="danger" title={t('billing.usage.loadError')}>
            <Button
              variant="secondary"
              size="sm"
              className="mt-2"
              onClick={() => {
                void usageQuery.refetch()
              }}
            >
              {t('retry')}
            </Button>
          </Alert>
        ) : usageQuery.data === null || usageQuery.data === undefined ? (
          <EmptyState
            icon={Lock}
            title={t('billing.noPermission.title')}
            description={t('billing.noPermission.description')}
          />
        ) : (
          <UsageDetailSection usage={usageQuery.data} tenantSlug={tenant.slug} />
        )}
      </section>

      {overview !== null ? (
        <>
          <ProfileSection tenantId={tenant.id} overview={overview} />
          <InvoicesSection tenantId={tenant.id} currency={overview.currency} />
          <CancelSection tenantId={tenant.id} overview={overview} />
        </>
      ) : null}
    </div>
  )
}

function UsageDetailSection({
  usage,
  tenantSlug,
}: {
  readonly usage: UsageDetail
  readonly tenantSlug: string
}) {
  const { t, i18n } = useTranslation()
  const bucketLabels = t('billing.usage.buckets.kinds', { returnObjects: true }) as Record<
    string,
    string
  >
  const kindLabels = t('billing.usage.byKind.kinds', { returnObjects: true }) as Record<
    string,
    string
  >
  const totalConsumed = usage.byKind.reduce((sum, k) => sum + k.credits, 0)

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>{t('billing.usage.buckets.title')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4 text-sm">
          {usage.buckets.length === 0 ? (
            <p className="text-foreground-muted">{t('billing.usage.buckets.empty')}</p>
          ) : (
            usage.buckets.map((bucket, index) => {
              const percent =
                bucket.credits > 0
                  ? Math.min(100, Math.round((bucket.consumed / bucket.credits) * 100))
                  : 0
              return (
                <div
                  key={`${bucket.kind}-${String(index)}`}
                  className="flex flex-col gap-1"
                  data-testid="usage-bucket"
                >
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="font-medium text-foreground-primary">
                      {translateEnumValue(bucketLabels, bucket.kind)}
                    </span>
                    <span className="text-xs text-foreground-muted">
                      {bucket.expiresAt === null
                        ? t('billing.usage.buckets.noExpiry')
                        : t('billing.usage.buckets.expiresAt', {
                            date: formatShortDate(bucket.expiresAt, i18n.language),
                          })}
                    </span>
                  </div>
                  <div
                    className="h-2 w-full overflow-hidden rounded-full bg-background-subtle"
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={percent}
                    aria-label={translateEnumValue(bucketLabels, bucket.kind)}
                  >
                    <div
                      className="h-full bg-action-primary"
                      style={{ width: `${String(percent)}%` }}
                    />
                  </div>
                  <span className="text-xs text-foreground-muted">
                    {t('billing.usage.buckets.consumedOf', {
                      consumed: formatCount(bucket.consumed, i18n.language),
                      credits: formatCount(bucket.credits, i18n.language),
                    })}
                  </span>
                </div>
              )
            })
          )}
        </CardContent>
      </Card>

      {totalConsumed === 0 ? (
        <EmptyState
          icon={Gauge}
          title={t('billing.usage.empty.title')}
          description={t('billing.usage.empty.description')}
          action={
            <Link
              to="/app/$tenantSlug/agents"
              params={{ tenantSlug }}
              className={buttonVariants({ variant: 'primary', size: 'sm' })}
            >
              {t('billing.usage.empty.action')}
            </Link>
          }
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>{t('billing.usage.byKind.title')}</CardTitle>
              <CardDescription>
                {t('billing.usage.window', { days: usage.windowDays })}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="flex flex-col gap-2 text-sm">
                {usage.byKind.map((kind) => (
                  <li key={kind.kind} className="flex items-center justify-between gap-4">
                    <span className="text-foreground-secondary">
                      {translateEnumValue(kindLabels, kind.kind)}
                    </span>
                    <span className="font-medium text-foreground-primary">
                      {formatCount(kind.credits, i18n.language)}
                    </span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>{t('billing.usage.daily.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <BarChart
                ariaLabel={t('billing.usage.daily.ariaLabel')}
                series={[
                  {
                    key: 'credits',
                    label: t('billing.usage.daily.series'),
                    colorClassName: 'bg-action-primary',
                  },
                ]}
                points={usage.daily.map((d) => ({
                  key: d.date,
                  label: formatShortDate(`${d.date}T12:00:00Z`, i18n.language),
                  values: { credits: d.credits },
                }))}
                formatValue={(value) => formatCount(value, i18n.language)}
              />
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}
